import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const TIP_PRESETS = [3, 5, 8, 12];

export default function RatingTipDialog({ booking, open, onOpenChange, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error("Pick a star rating first");
      return;
    }
    setSubmitting(true);
    try {
      const tipAmount = customTip ? parseFloat(customTip) || 0 : tip;
      const { data } = await axios.post(`${API}/bookings/${booking.id}/rate-tip`, {
        rating,
        comment: comment.trim(),
        tip_amount: Math.max(0, tipAmount),
      });
      toast.success(
        tipAmount > 0
          ? `Thanks! Tip of $${tipAmount.toFixed(2)} sent to your driver`
          : "Thanks for the rating!"
      );
      onSubmitted?.(data);
      onOpenChange(false);
      // Reset
      setRating(0);
      setHover(0);
      setComment("");
      setTip(0);
      setCustomTip("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md" data-testid="rating-tip-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "Outfit, system-ui" }}>
            How was your trip?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Stars */}
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                data-testid={`star-${n}`}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`w-9 h-9 transition-colors ${
                    (hover || rating) >= n
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-zinc-600"
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm text-white/60">
              {["", "Not great", "Could be better", "OK", "Good", "Excellent!"][rating]}
            </p>
          )}

          {/* Comment */}
          <Textarea
            placeholder="Tell us anything (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="bg-[#050505] border-white/10 text-white text-sm"
            rows={2}
            data-testid="rating-comment"
          />

          {/* Tip */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2">
              Add a tip (optional)
            </p>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {TIP_PRESETS.map((t) => (
                <button
                  key={t}
                  onClick={() => { setTip(t); setCustomTip(""); }}
                  data-testid={`tip-${t}`}
                  className={`py-2.5 rounded-lg border text-sm font-bold transition-colors ${
                    tip === t && !customTip
                      ? "bg-[#0047FF] text-white border-[#0047FF]"
                      : "bg-[#121212] text-white/70 border-white/10 hover:border-white/30"
                  }`}
                >
                  ${t}
                </button>
              ))}
            </div>
            <input
              type="number"
              min="0"
              step="0.5"
              placeholder="Custom amount"
              value={customTip}
              onChange={(e) => { setCustomTip(e.target.value); setTip(0); }}
              data-testid="tip-custom"
              className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0047FF]"
            />
            <p className="text-[10px] text-white/30 mt-1">
              100% goes to your driver. No fees.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="text-white/50 hover:bg-white/5"
            data-testid="rating-skip"
          >
            Skip
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || rating < 1}
            className="bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold disabled:opacity-40"
            data-testid="rating-submit"
          >
            {submitting ? "Sending…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
